import React, { useEffect } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Container, SectionTitle, SectionSubtitle } from "../../styles/Layout";
import {
  FaShieldAlt,
  FaLock,
  FaUserShield,
  FaDatabase,
  FaEye,
  FaCog,
  FaEnvelope,
} from "react-icons/fa";

const PrivacySection = styled.section`
  padding: var(--spacing-xxxl) 0;
  position: relative;
  overflow: hidden;
  min-height: 100vh;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
        circle at 30% 20%,
        rgba(0, 212, 255, 0.05) 0%,
        transparent 50%
      ),
      radial-gradient(
        circle at 70% 80%,
        rgba(255, 0, 128, 0.05) 0%,
        transparent 50%
      );
    z-index: 1;
  }
`;

const PrivacyContainer = styled(Container)`
  position: relative;
  z-index: 2;
`;

const ContentWrapper = styled(motion.div)`
  background: ${(props) => props.theme.gradients.glassCard};
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  padding: 3rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    margin: 1rem;
  }
`;

const Title = styled(SectionTitle)`
  background: linear-gradient(
    135deg,
    var(--neon-blue) 0%,
    var(--neon-magenta) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 2rem;
  text-align: left;
  letter-spacing: -0.02em;

  &:after {
    background: ${(props) => props.theme.gradients.neonBlue};
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Section = styled(motion.section)`
  margin-bottom: 3rem;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin-bottom: 2rem;
  }
`;

const PrivacySectionTitle = styled.h2`
  color: ${(props) => props.theme.colors.neonBlue};
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  padding-bottom: 0.8rem;
  border-bottom: 2px solid ${(props) => props.theme.colors.neonBlue};
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 50px;
    height: 2px;
    background: ${(props) => props.theme.gradients.neonMagenta};
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubTitle = styled.h3`
  color: ${(props) => props.theme.colors.neonGreen};
  font-size: 1.3rem;
  font-weight: 500;
  margin-bottom: 1rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const Paragraph = styled.p`
  margin-bottom: 1.2rem;
  font-size: 1rem;
  line-height: 1.7;
  color: ${(props) => props.theme.colors.textSecondary};

  strong {
    color: ${(props) => props.theme.colors.textPrimary};
    font-weight: 600;
  }

  a {
    color: ${(props) => props.theme.colors.neonBlue};
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: ${(props) => props.theme.colors.neonMagenta};
      text-decoration: underline;
    }
  }
`;

const List = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 2rem;

  @media (max-width: 768px) {
    padding-left: 1.5rem;
  }
`;

const ListItem = styled.li`
  margin-bottom: 0.8rem;
  color: ${(props) => props.theme.colors.textSecondary};
  position: relative;

  &::marker {
    color: ${(props) => props.theme.colors.neonBlue};
  }

  strong {
    color: ${(props) => props.theme.colors.textPrimary};
  }
`;

const ContactInfo = styled.div`
  background: ${(props) => props.theme.gradients.glassCard};
  border: 2px solid ${(props) => props.theme.colors.neonBlue};
  border-radius: 20px;
  padding: 2.5rem;
  margin-top: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${(props) => props.theme.gradients.neonBlue};
  }

  @media (max-width: 768px) {
    padding: 2rem;
    border-radius: 15px;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
  }
`;

const HighlightBox = styled.div`
  background: ${(props) => props.theme.gradients.glassHover};
  border-left: 4px solid ${(props) => props.theme.colors.neonMagenta};
  border-radius: 12px;
  padding: 2rem;
  margin: 2rem 0;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  transition: all 0.3s ease;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: ${(props) => props.theme.gradients.neonMagenta};
    filter: blur(2px);
  }

  &:hover {
    transform: translateX(5px);
    box-shadow: 0 10px 25px rgba(255, 0, 128, 0.2);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const LastUpdated = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e6fffa;
  color: #234e52;
  padding: 5px 15px;
  border-radius: 25px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid #b2f5ea;
  margin: 0 auto;
  width: fit-content;

  &::before {
    content: "🔒";
    margin-right: 8px;
    font-size: 1.1rem;
  }
`;

const PrivacyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xxxl);
  align-items: center;
  position: relative;
  z-index: 2;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
    text-align: center;
    min-height: auto;
  }
`;

const PrivacyHeaderContent = styled(motion.div)`
  max-width: 600px;
  text-align: left;

  @media (max-width: 968px) {
    max-width: 100%;
    order: 2;
    text-align: center;
  }
`;

const PrivacyVisual = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;

  @media (max-width: 968px) {
    order: 1;
    margin-bottom: var(--spacing-xl);
    min-height: 300px;
  }
`;

const FloatingCard = styled(motion.div)`
  position: absolute;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: var(--spacing-md);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${(props) => props.iconColor || "var(--neon-blue)"};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
  }

  .content {
    .title {
      font-size: 0.9rem;
      font-weight: 600;
      color: var(--text-primary);
      margin-bottom: 2px;
    }

    .subtitle {
      font-size: 0.7rem;
      color: var(--text-muted);
    }
  }
`;

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const floatingCards = [
    {
      top: "10%",
      left: "10%",
      icon: <FaShieldAlt />,
      iconColor: "var(--neon-blue)",
      title: "Data Protection",
      subtitle: "Secure & Safe",
    },
    {
      top: "20%",
      right: "15%",
      icon: <FaLock />,
      iconColor: "var(--neon-magenta)",
      title: "Encryption",
      subtitle: "End-to-End",
    },
    {
      bottom: "25%",
      left: "20%",
      icon: <FaUserShield />,
      iconColor: "var(--neon-green)",
      title: "User Privacy",
      subtitle: "Protected",
    },
    {
      bottom: "10%",
      right: "10%",
      icon: <FaDatabase />,
      iconColor: "var(--neon-blue)",
      title: "Data Storage",
      subtitle: "Compliant",
    },
  ];

  return (
    <PrivacySection>
      <PrivacyContainer>
        <PrivacyGrid>
          <PrivacyHeaderContent
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Title>Privacy Policy</Title>
            <SectionSubtitle>
              Your privacy is important to us. This policy explains how we
              collect, use, and protect your information.
            </SectionSubtitle>
          </PrivacyHeaderContent>

          <PrivacyVisual
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          >
            {floatingCards.map((card, index) => (
              <FloatingCard
                key={index}
                iconColor={card.iconColor}
                style={{
                  top: card.top,
                  bottom: card.bottom,
                  left: card.left,
                  right: card.right,
                }}
                animate={{
                  y: [0, -15, 0],
                  rotate: [0, 2, -2, 0],
                }}
                transition={{
                  duration: 3 + index * 0.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: index * 0.2,
                }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="icon">{card.icon}</div>
                <div className="content">
                  <div className="title">{card.title}</div>
                  <div className="subtitle">{card.subtitle}</div>
                </div>
              </FloatingCard>
            ))}
          </PrivacyVisual>
        </PrivacyGrid>
        <LastUpdated>
          <strong>Last updated:</strong> October 2025
        </LastUpdated>

        <ContentWrapper
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Section
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <PrivacySectionTitle>1. Introduction</PrivacySectionTitle>
            <Paragraph>
              Welcome to GeoSentry X Labs ("we," "our," or "us"). We are
              committed to protecting your privacy and ensuring the security of
              your personal information. This Privacy Policy explains how we
              collect, use, disclose, and safeguard your information when you
              visit our website and use our services.
            </Paragraph>
            <Paragraph>
              At GeoSentry X Labs, we understand the importance of your privacy
              and are dedicated to maintaining the confidentiality of your
              personal data. This policy outlines our practices regarding the
              collection, use, and protection of information that identifies you
              personally.
            </Paragraph>
          </Section>
          <Section
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <PrivacySectionTitle>2. Information We Collect</PrivacySectionTitle>

            <SubTitle>2.1 Personal Information</SubTitle>
            <Paragraph>
              We may collect the following types of personal information:
            </Paragraph>
            <List>
              <ListItem>
                Name and contact information (email address, phone number)
              </ListItem>
              <ListItem>Company information and job title</ListItem>
              <ListItem>
                Information you provide when contacting us or requesting
                services
              </ListItem>
              <ListItem>
                Technical information about your device and browser
              </ListItem>
            </List>

            <SubTitle>2.2 Automatically Collected Information</SubTitle>
            <Paragraph>
              When you visit our website, we may automatically collect:
            </Paragraph>
            <List>
              <ListItem>IP address and location data</ListItem>
              <ListItem>Browser type and version</ListItem>
              <ListItem>Pages visited and time spent on our website</ListItem>
              <ListItem>Referring website information</ListItem>
              <ListItem>
                Device information (mobile device ID, operating system)
              </ListItem>
            </List>
          </Section>
          <Section>
            <PrivacySectionTitle>
              3. How We Use Your Information
            </PrivacySectionTitle>
            <Paragraph>
              We use the collected information for the following purposes:
            </Paragraph>
            <List>
              <ListItem>To provide and maintain our services</ListItem>
              <ListItem>
                To respond to your inquiries and provide customer support
              </ListItem>
              <ListItem>To improve our website and services</ListItem>
              <ListItem>
                To send you updates about our products and services (with your
                consent)
              </ListItem>
              <ListItem>To comply with legal obligations</ListItem>
              <ListItem>
                To detect and prevent fraud or security breaches
              </ListItem>
            </List>
            <Paragraph>
              We ensure that your information is used only for legitimate
              business purposes and in accordance with applicable privacy laws.
              We do not use your personal information for automated
              decision-making without your explicit consent.
            </Paragraph>
          </Section>
          <Section>
            <PrivacySectionTitle>
              4. Information Sharing and Disclosure
            </PrivacySectionTitle>
            <Paragraph>
              We do not sell, trade, or otherwise transfer your personal
              information to third parties without your consent, except in the
              following circumstances:
            </Paragraph>
            <List>
              <ListItem>
                With service providers who assist us in operating our website
                and conducting our business
              </ListItem>
              <ListItem>
                When required by law or to respond to legal process
              </ListItem>
              <ListItem>
                To protect our rights, property, or safety, or that of others
              </ListItem>
              <ListItem>
                In connection with a business transfer or merger
              </ListItem>
            </List>
          </Section>
          <Section>
            <PrivacySectionTitle>5. Data Security</PrivacySectionTitle>
            <Paragraph>
              We implement appropriate technical and organizational security
              measures to protect your personal information against unauthorized
              access, alteration, disclosure, or destruction. However, no method
              of transmission over the internet is 100% secure, and we cannot
              guarantee absolute security.
            </Paragraph>
          </Section>
          <Section>
            <PrivacySectionTitle>
              6. Cookies and Tracking Technologies
            </PrivacySectionTitle>
            <Paragraph>
              Our website may use cookies and similar tracking technologies to
              enhance your browsing experience. You can control cookie settings
              through your browser preferences. Disabling cookies may affect the
              functionality of our website.
            </Paragraph>

            <HighlightBox>
              <SubTitle>Important Note About Cookies</SubTitle>
              <Paragraph style={{ marginBottom: 0 }}>
                We use essential cookies for website functionality and optional
                cookies for analytics and personalization. You can manage your
                cookie preferences at any time through your browser settings or
                our cookie preference center.
              </Paragraph>
            </HighlightBox>

            <SubTitle>6.1 Types of Cookies We Use</SubTitle>
            <List>
              <ListItem>
                <strong>Essential Cookies:</strong> Required for basic website
                functionality
              </ListItem>
              <ListItem>
                <strong>Analytics Cookies:</strong> Help us understand how
                visitors use our website
              </ListItem>
              <ListItem>
                <strong>Marketing Cookies:</strong> Used to deliver relevant
                advertisements
              </ListItem>
              <ListItem>
                <strong>Preference Cookies:</strong> Remember your settings and
                preferences
              </ListItem>
            </List>
          </Section>
          <Section>
            <PrivacySectionTitle>7. Your Rights</PrivacySectionTitle>
            <Paragraph>
              Depending on your location, you may have the following rights
              regarding your personal information:
            </Paragraph>
            <List>
              <ListItem>Access to your personal information</ListItem>
              <ListItem>Correction of inaccurate information</ListItem>
              <ListItem>Deletion of your personal information</ListItem>
              <ListItem>Objection to processing of your information</ListItem>
              <ListItem>Data portability</ListItem>
              <ListItem>Withdrawal of consent</ListItem>
            </List>
          </Section>
          <Section>
            <PrivacySectionTitle>8. Third-Party Links</PrivacySectionTitle>
            <Paragraph>
              Our website may contain links to third-party websites. We are not
              responsible for the privacy practices of these external sites. We
              encourage you to review the privacy policies of any third-party
              websites you visit.
            </Paragraph>
          </Section>
          <Section>
            <PrivacySectionTitle>9. Children's Privacy</PrivacySectionTitle>
            <Paragraph>
              Our services are not intended for children under the age of 13. We
              do not knowingly collect personal information from children under
              13. If we become aware that we have collected such information, we
              will take steps to delete it promptly.
            </Paragraph>
          </Section>
          <Section>
            <PrivacySectionTitle>
              10. Changes to This Privacy Policy
            </PrivacySectionTitle>
            <Paragraph>
              We may update this Privacy Policy from time to time to reflect
              changes in our practices or applicable laws. We will notify you of
              any material changes by posting the updated policy on our website
              and updating the "Last Updated" date.
            </Paragraph>
          </Section>
          <Section>
            <PrivacySectionTitle>
              11. International Data Transfers
            </PrivacySectionTitle>
            <Paragraph>
              If you are located outside of our primary operating jurisdiction,
              please note that your information may be transferred to and
              processed in countries with different data protection laws. We
              ensure appropriate safeguards are in place for such transfers.
            </Paragraph>
          </Section>
          <Section>
            <PrivacySectionTitle>12. Terms of Service</PrivacySectionTitle>
            <Paragraph>
              By using our services, you agree to comply with our Terms of
              Service. These terms govern your use of our website and services,
              and outline the rights and responsibilities of both parties.
            </Paragraph>

            <SubTitle>12.1 Service Availability</SubTitle>
            <Paragraph>
              We strive to maintain continuous service availability, but we
              cannot guarantee uninterrupted access to our services. We reserve
              the right to modify or discontinue services with reasonable
              notice.
            </Paragraph>

            <SubTitle>12.2 User Responsibilities</SubTitle>
            <List>
              <ListItem>Provide accurate and current information</ListItem>
              <ListItem>
                Maintain the security of your account credentials
              </ListItem>
              <ListItem>
                Use our services in compliance with applicable laws
              </ListItem>
              <ListItem>Respect intellectual property rights</ListItem>
            </List>
          </Section>{" "}
          <Section>
            <PrivacySectionTitle>13. Data Retention</PrivacySectionTitle>
            <Paragraph>
              We retain your personal information only as long as necessary to
              fulfill the purposes outlined in this Privacy Policy, comply with
              legal requirements, resolve disputes, and enforce our agreements.
            </Paragraph>

            <SubTitle>13.1 Retention Periods</SubTitle>
            <List>
              <ListItem>
                Account information: Retained while your account is active
              </ListItem>
              <ListItem>
                Communication records: Retained for 3 years after last contact
              </ListItem>
              <ListItem>
                Transaction data: Retained for 7 years for tax and legal
                compliance
              </ListItem>
              <ListItem>
                Marketing data: Retained until you opt-out or withdraw consent
              </ListItem>
            </List>
          </Section>
          <Section>
            <PrivacySectionTitle>
              14. Compliance and Certifications
            </PrivacySectionTitle>
            <Paragraph>
              GeoSentry X Labs is committed to maintaining the highest standards
              of data protection and privacy compliance. We regularly review and
              update our practices to ensure alignment with evolving regulations
              and industry best practices.
            </Paragraph>

            <SubTitle>14.1 Regulatory Compliance</SubTitle>
            <List>
              <ListItem>
                GDPR (General Data Protection Regulation) compliance for EU
                residents
              </ListItem>
              <ListItem>
                CCPA (California Consumer Privacy Act) compliance for California
                residents
              </ListItem>
              <ListItem>
                SOC 2 Type II compliance for security and availability
              </ListItem>
              <ListItem>
                ISO 27001 information security management standards
              </ListItem>
            </List>
          </Section>
          <ContactInfo>
            <PrivacySectionTitle>Contact Us</PrivacySectionTitle>
            <Paragraph>
              If you have any questions about this Privacy Policy or our privacy
              practices, please contact us at:
            </Paragraph>
            <Paragraph>
              <FaEnvelope />{" "}
              <a
                href="mailto:support@geosentryx.com"
                style={{ color: " #00d4ff" }}
              >
                support@geosentryx.com
              </a>
              <br />
            </Paragraph>
          </ContactInfo>
        </ContentWrapper>
      </PrivacyContainer>
    </PrivacySection>
  );
};

export default PrivacyPolicy;
